/**
 * LegendHeader Component
 * 
 * Renders a descriptive legend in the header of a positions table.
 * Dynamically generates legend items based on provided legend data.
 * 
 * @param {Object} props - Component props
 * @param {Array<Object>} props.legends - Array of legend items
 * @param {string} props.legends[].symbol - Symbol or abbreviation (e.g., 'X', 'Y', 'Z')
 * @param {string} props.legends[].description - Description of what the symbol means
 * @param {string} [props.className] - Additional CSS class for styling
 * 
 * @example
 * // Basic usage with three legend items
 * <LegendHeader 
 *   legends={[
 *     { symbol: 'X', description: 'Eliminado' },
 *     { symbol: 'Y', description: 'Clasificado' },
 *     { symbol: 'Z', description: 'Repechaje' }
 *   ]}
 * />
 * 
 * @example
 * // Usage with custom className
 * <LegendHeader 
 *   legends={[{ symbol: 'Q', description: 'Qualified' }]}
 *   className="custom-legend"
 * />
 */
export default function LegendHeader({ legends = [], className = '' }) {
  if (!legends.length) return null;

  return (
    <div className={`positions-table__header-legend ${className}`.trim()}>
      <dl>
        {legends.map(({ symbol, description }, index) => (
          <LegendItem 
            key={`${symbol}-${index}`}
            symbol={symbol}
            description={description}
          />
        ))}
      </dl>
    </div>
  );
}

/**
 * LegendItem Component
 * 
 * Renders a single legend item with symbol and description.
 * 
 * @param {Object} props - Component props
 * @param {string} props.symbol - Symbol or abbreviation
 * @param {string} props.description - Description text
 */
function LegendItem({ symbol, description }) {
  return (
    <div className="legend-item">
      <dt>{symbol}:</dt>
      <dd>{description}</dd>
    </div>
  );
}